/**
 * Moment arrivals - in-app announcement of incoming moments (BUG-031).
 *
 * The realtime plumbing already worked; nothing ever surfaced it. This is the
 * missing half: the WebSocket announces here, GlobalToast renders it.
 *
 * Same shape as vaultUploadQueue's emitter so GlobalToast can carry both
 * sources without inventing a second overlay system.
 */
import { useFriendsStore } from '../store/friendsStore';
import { useMessageStore } from '../store/messageStore';
import { useSettingsStore } from '../store/settingsStore';
import { useToastStore } from '../store/toastStore';
import type { Message } from '../types';

export interface MomentArrival {
  messageId: number;
  senderId: number;
  senderName: string;
  avatarUrl: string | null;
  /** "sent you a moment" vs "sent you a message" */
  isMoment: boolean;
}

type ArrivalListener = (arrival: MomentArrival) => void;

const listeners = new Set<ArrivalListener>();

export function onMomentArrival(listener: ArrivalListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Who sent this? Resolved from the device's own friends list. (A new_message
 * frame actually carries the full MessageResponse — sender display fields
 * excepted — so the name still has to come from here, where any nickname the
 * user chose lives anyway.)
 */
function resolveSender(senderId: number): { name: string; avatarUrl: string | null } {
  const friendship = useFriendsStore
    .getState()
    .friends.find((f) => f.friend.id === senderId);
  if (friendship) {
    return {
      name:
        friendship.nickname ||
        friendship.friend.display_name ||
        friendship.friend.username,
      avatarUrl: friendship.friend.avatar_url,
    };
  }

  const conversation = useMessageStore
    .getState()
    .inbox.find((c) => c.friend_id === senderId);
  if (conversation) {
    return {
      name: conversation.friend.display_name || conversation.friend.username,
      avatarUrl: conversation.friend.avatar_url,
    };
  }

  return { name: 'Someone', avatarUrl: null };
}

/** Already looking at this exact conversation? Then it isn't news. */
function isConversationOpen(senderId: number): boolean {
  return window.location.pathname === `/chat/${senderId}`;
}

export function announceArrival(message: Message): void {
  if (!message?.sender_id) return;

  // One switch, honoured everywhere — the same setting the server checks
  // before sending a push.
  if (useSettingsStore.getState().settings?.notifications_enabled === false) return;

  if (isConversationOpen(message.sender_id)) return;

  const { name, avatarUrl } = resolveSender(message.sender_id);
  const arrival: MomentArrival = {
    messageId: message.id,
    senderId: message.sender_id,
    senderName: name,
    avatarUrl,
    isMoment: message.message_type !== 'text',
  };

  listeners.forEach((listener) => listener(arrival));
}

/**
 * FEAT-040 w2: the person you trusted saved your moment. Foreground half of
 * the sender notification — the backgrounded half is the server's push.
 */
export function announceMomentSaved(saverId: number): void {
  if (useSettingsStore.getState().settings?.notifications_enabled === false) return;

  const { name } = resolveSender(saverId);
  useToastStore.getState().show(`${name} saved your moment`);
}
